import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { GradeDocument, GradeQuality } from '../../models/grade.models';

export interface EditDocumentDialogData {
  document: GradeDocument;
  title?: string;
}

export interface EditDocumentDialogResult {
  action: 'save' | 'cancel';
  data?: Partial<GradeDocument>;
}

@Component({
  selector: 'app-modal-edit-document',
  templateUrl: './modal-edit-document.component.html',
  styleUrls: ['./modal-edit-document.component.scss']
})
export class ModalEditDocumentComponent implements OnInit {
  editForm: FormGroup;

  // Dropdown options
  gradeQualityOptions: GradeQuality[] = ['very_low', 'low', 'moderate', 'high'];
  riskOfBiasOptions = ['very_high', 'high', 'moderate', 'low'];
  studyTypeOptions = [
    'Systematic Review',
    'Meta-Analysis',
    'Randomized Controlled Trial',
    'Cohort Study',
    'Case-Control Study',
    'Cross-Sectional Study',
    'Case Series',
    'Case Report',
    'Expert Opinion',
    'Other'
  ];

  constructor(
    public dialogRef: MatDialogRef<ModalEditDocumentComponent, EditDocumentDialogResult>,
    @Inject(MAT_DIALOG_DATA) public data: EditDocumentDialogData,
    private formBuilder: FormBuilder
  ) {
    this.editForm = this.createForm();
  }

  ngOnInit(): void {
    this.populateForm();
  }

  private createForm(): FormGroup {
    return this.formBuilder.group({
      authors: ['', [Validators.required]],
      year: ['', [Validators.required, Validators.min(1900), Validators.max(new Date().getFullYear())]],
      study_type: ['', [Validators.required]],
      main_outcome: ['', [Validators.required]],
      precision: ['', [Validators.required]],
      consistency: ['', [Validators.required]],
      risk_of_bias: ['', [Validators.required]],
      grade_quality: ['', [Validators.required]]
    });
  }

  private populateForm(): void {
    if (this.data.document) {
      console.log('Populating form with document data:', this.data.document);
      this.editForm.patchValue({
        authors: this.data.document.authors || '',
        year: this.data.document.year || '',
        study_type: this.data.document.study_type || '',
        main_outcome: this.data.document.main_outcome || '',
        precision: this.data.document.precision || '',
        consistency: this.data.document.consistency || '',
        risk_of_bias: this.data.document.risk_of_bias || '',
        grade_quality: this.data.document.grade_quality || ''
      });
      console.log('Form values after populate:', this.editForm.value);
    }
  }

  onSave(): void {
    if (this.editForm.valid) {
      const result: EditDocumentDialogResult = {
        action: 'save',
        data: {
          ...this.editForm.value,
          id: this.data.document.id,
          title: this.data.document.title // Keep existing title
        }
      };
      this.dialogRef.close(result);
    } else {
      // Mark all fields as touched to show validation errors
      this.editForm.markAllAsTouched();
    }
  }

  onCancel(): void {
    const result: EditDocumentDialogResult = {
      action: 'cancel'
    };
    this.dialogRef.close(result);
  }

  // Helper methods for display
  formatGradeQuality(value: string): string {
    return value.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  }

  formatRiskOfBias(value: string): string {
    return value.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  }

  getFormErrorMessage(fieldName: string): string {
    const field = this.editForm.get(fieldName);
    if (field?.hasError('required')) {
      return `${this.getFieldDisplayName(fieldName)} is required`;
    }
    if (field?.hasError('min')) {
      return 'Year must be 1900 or later';
    }
    if (field?.hasError('max')) {
      return 'Year cannot be in the future';
    }
    return '';
  }

  private getFieldDisplayName(fieldName: string): string {
    const displayNames: { [key: string]: string } = {
      authors: 'Authors',
      year: 'Year',
      study_type: 'Study Type',
      main_outcome: 'Main Outcome',
      precision: 'Precision',
      consistency: 'Consistency',
      risk_of_bias: 'Risk of Bias',
      grade_quality: 'Grade Quality'
    };
    return displayNames[fieldName] || fieldName;
  }

  // Helper methods for quality colors and icons
  getQualityColor(quality: string): string {
    const colors: { [key: string]: string } = {
      very_low: '#f44336',    // Red
      low: '#ff9800',         // Orange
      moderate: '#2196f3',    // Blue
      high: '#4caf50'         // Green
    };
    return colors[quality] || '#666';
  }

  getQualityIcon(quality: string): string {
    const icons: { [key: string]: string } = {
      very_low: 'sentiment_very_dissatisfied',
      low: 'sentiment_dissatisfied',
      moderate: 'sentiment_satisfied',
      high: 'sentiment_very_satisfied'
    };
    return icons[quality] || 'help';
  }

  getRiskColor(risk: string): string {
    const colors: { [key: string]: string } = {
      very_high: '#f44336',   // Red
      high: '#ff9800',        // Orange
      moderate: '#2196f3',    // Blue
      low: '#4caf50'          // Green
    };
    return colors[risk] || '#666';
  }

  getRiskIcon(risk: string): string {
    const icons: { [key: string]: string } = {
      very_high: 'dangerous',
      high: 'warning',
      moderate: 'info',
      low: 'check_circle'
    };
    return icons[risk] || 'help';
  }
}